import { useState, useMemo } from 'react';
import {
  Box,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Avatar,
  Autocomplete,
} from '@mui/material';
import { Add as AddIcon, Delete } from '@mui/icons-material';
import { toast } from 'react-toastify';
import ListView from '@/components/datagrid/ListView';
import { useRouteLoaderData } from 'react-router-dom';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import de from 'date-fns/locale/de';
import useLiveData from '@/hooks/useLiveData';
import useApiQuery from '@/hooks/useApiQuery';
import api from '@/services/ApiService';

export default function VisitorsPage() {
  const { users = [] } = useRouteLoaderData('auth') ?? { users: [] };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [visitorName, setVisitorName] = useState('');
  const [responsibleUserId, setResponsibleUserId] = useState('');
  const [scheduledArrival, setScheduledArrival] = useState(new Date());
  const [selectedRows, setSelectedRows] = useState([]);

  const { isPending, data: visitors = [] } = useApiQuery(
    'visitor',
    {
      sort: { scheduledArrival: -1 },
    },
    true,
  );
  useLiveData('visitor');

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setVisitorName('');
    setComment('');
    setResponsibleUserId('');
    setScheduledArrival(new Date());
  };

  const handleScheduledArrivalChange = (newValue) => {
    if (newValue instanceof Date && !isNaN(newValue.getTime())) {
      setScheduledArrival(newValue);
    }
  };

  const handleAddVisitor = async () => {
    if (!visitorName.trim() || !responsibleUserId) {
      toast.warning('Bitte füllen Sie alle Felder aus.', {
        position: 'bottom-right',
        autoClose: 1000,
      });
      return;
    }
    try {
      const response = await api.post('/visitor', {
        name: visitorName,
        comment,
        responsibleUserId,
        scheduledArrival: scheduledArrival.toISOString(),
      });

      if (response.status === 201 || response.status === 200) {
        toast.success('Besucher erfolgreich hinzugefügt!', {
          position: 'bottom-right',
          autoClose: 1000,
        });
        handleCloseDialog();
      } else {
        toast.error('Unerwartete Antwort vom Server.', {
          position: 'bottom-right',
          autoClose: 1000,
        });
      }
    } catch {
      toast.error('Fehler beim Hinzufügen des Besuchers.', {
        position: 'bottom-right',
        autoClose: 1000,
      });
    }
  };

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    [],
  );

  const timeFmt = useMemo(
    () =>
      new Intl.DateTimeFormat('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }),
    [],
  );

  const columns = [
    {
      field: 'name',
      headerName: 'Name des Besuchers',
      flex: 2,
    },
    {
      field: 'responsibleUserId',
      headerName: 'Verantwortlicher Benutzer',
      flex: 2,
      valueGetter: (params) => {
        const user = users.find((u) => u._id === params.value);
        return user
          ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
          : '';
      },
      renderCell: (params) => {
        const user = users.find((u) => u._id === params.row.responsibleUserId);
        const fullName =
          user && (user.firstName || user.lastName)
            ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
            : '--- ---';
        return (
          <Box sx={{ display: 'flex', flexFlow: 'row', alignItems: 'center' }}>
            <Avatar
              src={
                user?.profilePicture
                  ? `${import.meta.env.VITE_FILE_URL}/${user.profilePicture}`
                  : undefined
              }
              sx={{ width: 32, height: 32, mr: 1 }}
            />
            {fullName}
          </Box>
        );
      },
    },
    {
      field: 'comment',
      headerName: 'Grund des Besuchs',
      flex: 3,
    },

    {
      field: 'time',
      headerName: 'Uhrzeit',
      flex: 1,
      renderCell: (params) => {
        const v = params?.row?.scheduledArrival;
        if (!v) return '--- ---';
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) return '--- ---';
        return `${timeFmt.format(d)} Uhr`;
      },
    },
    {
      field: 'date',
      headerName: 'Datum',
      flex: 1,
      renderCell: (params) => {
        const v = params?.row?.scheduledArrival;
        if (!v) return '--- ---';
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) return '--- ---';
        return dateFmt.format(d);
      },
    },
  ];

  const actions = [
    {
      label: 'Hinzufügen',
      icon: AddIcon,
      onClick: handleOpenDialog,
    },
    {
      label: 'Löschen',
      selection: 'multi',
      icon: Delete,
      onClick: async () => {
        await Promise.all(
          selectedRows.map(async (id) => {
            await api.delete(`/visitor/${id}`);
          }),
        );
      },
      color: 'error',
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
      <Box sx={{ boxSizing: 'border-box', height: '100%', width: '100%' }}>
        <ListView
          rows={visitors}
          columns={columns}
          loading={isPending}
          actions={actions}
          setSelectedRows={setSelectedRows}
          selectedRows={selectedRows}
        />

        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          sx={{ '& .MuiPaper-root': { width: '600px', maxWidth: '95%' } }}
        >
          <DialogTitle>Neuen Besucher hinzufügen</DialogTitle>
          <DialogContent
            sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
          >
            <TextField
              autoFocus
              label="Name des Besuchers"
              fullWidth
              value={visitorName}
              onChange={(e) => setVisitorName(e.target.value)}
              sx={{ mt: 1 }}
            />

            <Autocomplete
              options={users}
              getOptionLabel={(option) =>
                `${option?.firstName ?? ''} ${option?.lastName ?? ''}`.trim()
              }
              isOptionEqualToValue={(option, value) => option._id === value._id}
              value={users.find((u) => u._id === responsibleUserId) || null}
              onChange={(_e, newValue) =>
                setResponsibleUserId(newValue?._id ?? '')
              }
              renderInput={(params) => (
                <TextField {...params} label="Rückruf durch" />
              )}
            />

            <TextField
              label="Grund des Besuchs"
              fullWidth
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              multiline
              rows={4}
            />

            <DateTimePicker
              label="Ankunftszeit"
              value={scheduledArrival}
              onChange={handleScheduledArrivalChange}
              slotProps={{ textField: { fullWidth: true } }}
              sx={{ width: '100%' }}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseDialog}>Abbrechen</Button>
            <Button onClick={handleAddVisitor} variant="contained">
              Hinzufügen
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}
